import { useQuery } from '@apollo/client';
import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useBoolean,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
// import { usePriceToDinero ,priceToDineroWithCurrency} from '@/utilities/priceToDinero';
import { dinero } from 'dinero.js';
import { set } from 'lodash';
import * as React from 'react';

import { currencyDisplay } from '@/components/Currency/Currency';
import { Pagination } from '@/components/Pagination';
import { TableEmpty } from '@/components/Table';
import {
  AddItemToCartInput,
  CartItemType,
  EInvoiceStatus,
  OtherItemStatus,
  OtherItemVisibility,
  QuantityMode,
} from '@/graphql-types/globalTypes';
import { GetInvoicesFiltersInput } from '@/graphql-types/globalTypes';
import { Card } from '@/imports/ui/chakra/components/Card';
import {
  PlusIcon,
  SearchIcon,
  ShoppingCartIcon,
} from '@/imports/ui/chakra/feather';
import ItemCountManager from '@/imports/ui/components/Cart/OtherItems/ItemContManager';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';
import { useCart } from '@/state/cart';

import { GET_OTHER_ITEMS } from '../../graphql';
import {
  GetOtherItems,
  GetOtherItems_getOtherItems_results,
} from '../../graphql-types/GetOtherItems';
import { useChangeOtherItemStatus } from '../../hooks/useChangeOtherItemStatus';
import { MenuSelect, MenuSelectOption } from '../Common/MenuSelect';
import { SkeletonTableBody } from '../Common/SkeletonTableBody';
import CreateOtherItemModal from '../Form/CreateOtherItemModal';
import { OtherItemsListActions } from './OtherItemsListActions';

const pageSizeOptions = [10, 25, 50];

const statusOptions: MenuSelectOption[] = [
  { label: 'All', value: '' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
];

const getVisibilityTagProps = (
  visibility?: OtherItemVisibility | null
): { label: string; color: string; bg: string } => {
  switch (visibility) {
    case OtherItemVisibility.MEMBERS:
      return { label: 'MEMBERS', color: 'orange.500', bg: 'orange.50' };
    case OtherItemVisibility.ADMINS:
      return { label: 'ADMINS', color: 'red.500', bg: 'red.50' };
    case OtherItemVisibility.PUBLIC:
    default:
      return { label: 'PUBLIC', color: 'green.500', bg: 'green.50' };
  }
};

const showToast = (
  toast: ReturnType<typeof useToast>,
  title: string,
  description: string,
  status: 'success' | 'error',
  duration = 2000
) => {
  toast({
    title,
    description,
    status,
    duration,
    isClosable: true,
    // position: 'top-right'
  });
};

export const OtherItemsList: React.FC<{ isMember: boolean }> = ({
  isMember,
}) => {
  // this mutation for the add other item to cart
  const { items, addToCart, updateQuantity, refreshCart, removeFromCart } =
    useCart();

  // const { currency } = useDefaultCurrency();
  const toast = useToast();      
  const [page, setPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');
  const debouncedSearchValue = useDebouncedValue(searchInput, 500);
  const [filter, setFilter] = React.useState<GetInvoicesFiltersInput>({
    status: null,
  });
  const [mode, setMode] = React.useState<'create' | 'edit' | 'view'>('create'); // create | edit | view
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  // this state for create other items open model
  const [isCreateOtherItemModelOpen, setIsCreateOtherItemModelOpen] =
    useBoolean(false);

  // status toggle state
  const [isStatusChecked, setIsStatusChecked] = React.useState(false);
  const [selectedItem, setSelectedItem] =
    React.useState<GetOtherItems_getOtherItems_results | null>(null);

  const [addingItemId, setAddingItemId] = React.useState<string | null>(null);

  // state for manage added other items and counts
  const [quantities, setQuantities] = React.useState<{ [key: string]: number }>(
    {}
  );
  // State manage for the loading state while item increment or decrement
  const [loadingStates, setLoadingStates] = React.useState<{
    [id: string]: { inc: boolean; dec: boolean };
  }>({});

  // const priceToDinero = usePriceToDinero();
  // this is for the query variables
  const listQueryVariables = React.useMemo(
    () => ({
      input: {
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        search,
        filter: isMember ? { status: 'ACTIVE' } : filter?.status ? filter : {},
      },
    }),
    [itemsPerPage, page, search, filter]
  );

  // here is the query to get the data
  const { data, loading, error } = useQuery<GetOtherItems>(GET_OTHER_ITEMS, {
    variables: listQueryVariables,
    fetchPolicy: 'cache-and-network',
  });
  // this hooks for the change status of the other items
  const { changeStatus, loading: statusChanging } = useChangeOtherItemStatus();

  // List Data Variables
  const rows: GetOtherItems_getOtherItems_results[] =
    data?.getOtherItems?.results || [];
  // handle status toggle
  const onToggleStatus = React.useCallback(
    async (row: GetOtherItems_getOtherItems_results) => {
      const nextStatus = row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      // this logic added for the optimistic UI update in the cache
      try {
        const res = await changeStatus(
          { _id: row._id },
          {
            optimisticResponse: {
              changeOtherItemStatus: {
                __typename: 'ChangeOtherItemStatusOutput',
                success: true,
                message: 'Status updated',
              },
            },
            
            update(cache) {
              const id = cache.identify({
                __typename: 'OtherItem',
                _id: row._id,
              });

              cache.modify({
                id,
                fields: {
                  status() {
                    return nextStatus;
                  },
                },
              });
            },
          }
        );

        if (!res.data?.changeOtherItemStatus?.success) {
          throw new Error('Failed to change status');
        }

        toast({
          title: 'Success',
          description: 'Status updated successfully',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } catch (err: any) {
        toast({
          title: 'Error',
          description: err.message || 'Unable to change status',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [changeStatus, toast]
  );

  // handle filter change
  const handleFilterChange = React.useCallback(
    (newFilter: any) => {
      const updatedFilter = { ...filter, isDraft: false, ...newFilter };
      if (!updatedFilter.isDraft) delete updatedFilter.isDraft;
      setFilter(updatedFilter);
      setPage(1);
    },
    [filter]
  );

  // When user changes debounced search value, update search state
  React.useEffect(() => {
    setSearch(debouncedSearchValue);
    setPage(1);
  }, [debouncedSearchValue]);

  React.useEffect(() => {
    setPage(1);
  }, [itemsPerPage]);

  //Pagination counts and calculations
  const currentTotalCount = data?.getOtherItems?.totalCount || 0;
  const totalPages = Math.ceil(currentTotalCount / itemsPerPage);

  // handle edit action
  const onEdit = React.useCallback(
    (row: GetOtherItems_getOtherItems_results) => {
      setMode('edit');
      setSelectedItem(row); // 👈 edit mode
      setIsCreateOtherItemModelOpen.on();
    },
    [setIsCreateOtherItemModelOpen]
  );

  // handle view action
  const onView = React.useCallback(
    async (id: GetOtherItems_getOtherItems_results) => {
      // Your view logic here
      setSelectedItem(id);
      setMode('view');
      setIsCreateOtherItemModelOpen.on();
    },
    []
  );

  React.useEffect(() => {
    if (error) {
      showToast(
        toast,
        'Error',
        error.message || 'Failed to load promo codes',
        'error',
        5000
      );
    }
  }, [error, toast]);

  const updateLoadingState = (
    id: string,
    type: 'inc' | 'dec',
    value: boolean
  ) => {
    setLoadingStates((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [type]: value },
    }));
  };

  // Handle Add button
  const handleAdd = React.useCallback(async (id: string) => {
    try {
      const cartItem: AddItemToCartInput = {
        itemId: id,
        itemType: CartItemType.OTHER_ITEM,
        otherItemId: id,
        quantity: 1,
        quantityMode: QuantityMode.ADD,
      };
      // Wait for the add to cart mutation to complete
      await addToCart([cartItem]);
      setQuantities((prev) => ({
        ...prev,
        [id]: (prev[id] || 0) + 1,
      }));
      // Show success toast after mutation completes
      toast({
        title: 'Added to Cart',
        description: 'Other item has been successfully added to your cart.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to add other item to cart. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, []);
  // Safe Handle Add button to prevent multiple clicks while other item is being added
  const handleAddSafe = async (itemId: string) => {
    // already processing → ignore click
    if (addingItemId === itemId) return;

    setAddingItemId(itemId);

    try {
      await handleAdd(itemId); // 👈 tera existing logic
    } finally {
      setAddingItemId(null);
    }
  };

  // Increment item
  const onIncrement = React.useCallback(
    async (id: string) => {
      updateLoadingState(id, 'inc', true);
      const cartItemId =
        items?.find(
          (item: any) =>
            item.type === CartItemType.OTHER_ITEM && item.itemId === id
        )?._id ?? '';
      try {
        await updateQuantity({
          cartItemId: cartItemId,
          quantity: (quantities[id] || 0) + 1,
        });
        await refreshCart();
        setQuantities((prev) => ({
          ...prev,
          [id]: (prev[id] || 0) + 1,
        }));
      } finally {
        setTimeout(() => updateLoadingState(id, 'inc', false), 300);
      }
      // setTimeout(() => updateLoadingState(id, "inc", false), 300);
    },
    [updateLoadingState]
  );

  // Decrement item
  const onDecrement = React.useCallback(
    async (id: string) => {
      updateLoadingState(id, 'dec', true);
      const cartItemId =
        items?.find(
          (item: any) =>
            item.type === CartItemType.OTHER_ITEM && item.itemId === id
        )?._id ?? '';
      try {
        const qty = (quantities[id] || 0) - 1;
        if (qty <= 0) {
          try {
            await removeFromCart(cartItemId);
            toast({
              title: 'Removed from Cart',
              description:
                'Cart item has been successfully removed from your cart.',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          } catch (error) {
            toast({
              title: 'Error',
              description:
                error instanceof Error
                  ? error.message
                  : 'Failed to remove item from cart. Please try again.',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }
        } else {
          await updateQuantity({
            cartItemId: cartItemId,
            quantity: (quantities[id] || 0) - 1,
          });
        }
        refreshCart();
        setQuantities((prev) => {
          if (qty <= 0) {
            const copy = { ...prev };
            delete copy[id];
            return copy;
          }
          return { ...prev, [id]: qty };
        });
      } finally {
        setTimeout(() => updateLoadingState(id, 'dec', false), 300);
      }
      // setTimeout(() => updateLoadingState(id, "dec", false), 300);
    },
    [updateLoadingState]
  );

  // when user get the cart items then set the quantities
  React.useEffect(() => {
    // Initialize quantities from cart items
    const initialQuantities: { [key: string]: number } = {};
    items?.forEach((item: any) => {
      if (item.type === CartItemType.OTHER_ITEM && item.itemId) {
        initialQuantities[item.itemId] = item.quantity;
      }
    });
    setQuantities(initialQuantities);
  }, [items]);

  return (
    <VStack spacing={6} align="stretch">
      <Card bg="white" p={6}>
        {/* Search + action */}
        {!isMember && (
          <Flex justify="space-between" mb={6} align="center">
            <Text fontSize="xl" fontWeight="semibold">
              Other Items
            </Text>
          </Flex>
        )}
        <Flex
          justify="space-between"
          align={{ base: 'left', lg: 'center' }}
          gap={4}
          flexDirection={{ base: 'column', md: 'column', lg: 'row' }}
          flexWrap="wrap"
          mb={6}
        >
          {/* Search Input */}
          <Flex
            align={{ base: 'left', lg: 'center' }}
            justify={{ base: 'left', lg: 'space-between' }}
            flexWrap="wrap"
            flexDirection={{ base: 'column', md: 'column', lg: 'row' }}
            gap={4}
          >
            <Box>
              <InputGroup width={{ lg: '320px', sm: '250px' }}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder={`Search by Item Name`}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  bg="white"
                  borderColor="gray.300"
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px #3182ce',
                  }}
                />
              </InputGroup>
            </Box>

            {/* Status Filter */}
            {!isMember && (
              <Box w="150px">
                {/* status dropdown , default is all */}
                <MenuSelect
                  value={
                    filter.status
                      ? filter.status
                      : filter.isDraft
                      ? 'draft'
                      : ''
                  }
                  onChange={(value: any) => {
                    if (value === '') {
                      handleFilterChange({ status: null });
                    } else if (value === 'draft') {
                      handleFilterChange({ isDraft: true, status: null });
                    } else {
                      handleFilterChange({ status: value as EInvoiceStatus });
                    }
                  }}
                  options={statusOptions}
                  maxHeight={'100%'}
                />
              </Box>
            )}
          </Flex>
          {/* Create Other Item Button */}
          {!isMember && (
            <Button
              onClick={() => {
                setMode('create');
                setSelectedItem(null);
                setIsCreateOtherItemModelOpen.on();
              }}
              w="max-content"
            >
              Create Other Item
            </Button>
          )}
        </Flex>

        {/* Table */}
        <Box overflow="hidden" pb={6}>
          <Box overflowX="auto" minH="40vh" borderRadius="md">
            <Table size="sm">
              <Thead>
                <Tr
                  borderBottom="1px"
                  // color={textColor}
                  borderColor={borderColor}
                >
                  <Th py={3}>Item Name</Th>
                  <Th py={3}>Item Price</Th>
                  {isMember && <Th py={3}>Description</Th>}
                  {!isMember && (
                    <>
                      <Th py={3}>Total Unit</Th>
                      <Th py={3}>Visibility</Th>
                      <Th py={3}>Status</Th>
                    </>
                  )}
                  <Th py={3} textAlign={'center'}>
                    Actions
                  </Th>
                </Tr>
              </Thead>
              {loading ? (
                <SkeletonTableBody
                  columnCount={isMember ? 4 : 6}
                  useFullWidth={true}
                  rows={10}
                />
              ) : error ? (
                <Tbody>
                  <Tr>
                    <Td colSpan={isMember ? 4 : 6}>
                      <Text color="red.500">Failed to load other items</Text>
                    </Td>
                  </Tr>
                </Tbody>
              ) : rows.length > 0 ? (
                <Tbody>
                  {rows.map((row: GetOtherItems_getOtherItems_results) => {
                    const quantity = quantities[row._id] || 0;
                    const visibilityTag = getVisibilityTagProps(row.visibility);
                    return (
                      <Tr key={row._id} _hover={{ bg: hoverBg }}>
                        <Td>
                          <Tooltip label={row.name ?? 'Item Name'}>
                            <Text
                              maxW="200px"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                            >
                              {row.name ?? '-'}
                            </Text>
                          </Tooltip>
                        </Td>
                        {/* <Td>{formatCurrency(Number(row?.price?.amount),row?.price?.currency.code)}</Td> */}
                        <Td>{currencyDisplay(dinero(row?.price))}</Td>
                        {isMember && (
                          <Td maxW={'150px'}>{row?.description ?? '-'}</Td>
                        )}
                        {!isMember && (
                          <>
                            <Td>{!row.isLimitedUnits ? '-' : row.units}</Td>
                            <Td>
                              <Badge
                                color={visibilityTag.color}
                                bg={visibilityTag.bg}
                                borderRadius="base"
                                px={2}
                                py={0.5}
                                fontSize="10px"
                                fontWeight="bold"
                                textTransform="uppercase"
                              >
                                {visibilityTag.label}
                              </Badge>
                            </Td>
                            <Td>
                              <Switch
                                colorScheme="primary"
                                isChecked={
                                  row.status === OtherItemStatus.ACTIVE
                                }
                                onChange={() => onToggleStatus(row)}
                                size="lg"
                              />
                            </Td>
                          </>
                        )}
                        <Td py={2} width="180px" textAlign={'center'}>
                          {!isMember ? (
                            <OtherItemsListActions
                              row={row}
                              onEdit={() => onEdit(row)}
                              onView={() => onView(row)}
                            />
                          ) : (
                            <>
                              <Flex
                                justify="center"
                                gap={2}
                                textAlign={'center'}
                              >
                                {/* this code is for show the limited units badge and add button or item count manager based on the quantity in the cart */}
                                {/* {(Number(row.units) <= 5) && row.isLimitedUnits &&
                                        <Badge
                                          size="xl"
                                          textColor="red.500"
                                          backgroundColor="red.50"
                                          fontSize="14px" p={"4px 10px"}
                                          borderRadius="6px"
                                          textTransform="none"
                                          height={'fit-content'}
                                        >
                                          {row.units} left
                                        </Badge>
                                      } */}
                                {quantity === 0 ? (
                                  // <ShoppingCartIcon color="primary.500" fontSize="20" cursor="pointer" onClick={() => handleAdd(row._id)} />

                                  <Button
                                    aria-label={`Add ${row.name}`}
                                    size="sm"
                                    colorScheme="primary"
                                    // disabled={
                                    //   (Number(row.units) === 0 &&
                                    //     row.isLimitedUnits) ||
                                    //   addingItemId === row._id
                                    // }
                                    onClick={() => handleAddSafe(row._id)}
                                    isLoading={addingItemId === row._id}
                                    data-testid={`add-${row._id}`}
                                    flexShrink={0}
                                  >
                                    <PlusIcon boxSize={3} mr={1} />
                                    ADD
                                  </Button>
                                ) : (
                                  <Flex textAlign="left">
                                    <ItemCountManager
                                      item={{
                                        ...row,
                                        quantity,
                                        itemId: row._id,
                                      }}
                                      isIncrementing={
                                        loadingStates[row._id]?.inc || false
                                      }
                                      isDecrementing={
                                        loadingStates[row._id]?.dec || false
                                      }
                                      onIncrement={() => onIncrement(row._id)}
                                      onDecrement={() => onDecrement(row._id)}
                                      onRemove={() =>
                                        setQuantities((prev) => ({
                                          ...prev,
                                          [row._id]: 0,
                                        }))
                                      }
                                    />
                                  </Flex>
                                )}
                              </Flex>
                            </>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              ) : (
                <TableEmpty>There are no other items found</TableEmpty>
              )}
            </Table>
          </Box>

          {/* Pagination */}
          <Flex
            justify={
              Math.ceil(currentTotalCount / itemsPerPage) > 1
                ? 'space-between'
                : 'center'
            }
            align="center"
            gap={4}
            flexWrap="wrap"
          >
            {currentTotalCount > itemsPerPage ? (
              <Box display="flex" alignItems="center" mt={6}>
                <Box as="span" fontSize="sm" mr={2}>
                  Showing
                </Box>
                <Select
                  size="sm"
                  width="70px"
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  mr={2}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Select>
                <Box as="span" fontSize="sm">
                  of {currentTotalCount ?? 0} entries
                </Box>
              </Box>
            ) : (
              <Box as="span" fontSize="sm" mt={2}>
                Showing total {currentTotalCount ?? 0} entries
              </Box>
            )}
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                numberOfPages={Math.ceil(currentTotalCount / itemsPerPage)}
                onChange={(page) => setPage(page)}
              />
            )}
          </Flex>
        </Box>
      </Card>
      {
        isCreateOtherItemModelOpen && (
          <CreateOtherItemModal
            isOpen={isCreateOtherItemModelOpen}
            onClose={() => {
              setSelectedItem(null);
              setIsCreateOtherItemModelOpen.off();
            }}
            item={selectedItem}
            mode={mode}
          />
        ) //--- IGNORE ---
      }
    </VStack>
  );
};
